'use client';

import { ChevronRight, Users, Wallet, Clock, UserPlus, Percent, TrendingUp } from 'lucide-react';

interface BookingPipelineProps {
  // Pipeline data
  created: number;
  confirmed: number;
  inProgress: number;
  completed: number;
  cancelled: number;
  noShow: number;
  // Additional metrics
  revenue?: number;
  walkIns?: number;
  customers?: number;
  avgTicket?: number;
  utilization?: number;
  activeBarbersNow?: number;
  totalChairs?: number;
  queueLength?: number;
  avgWaitTime?: number;
  formatCurrency?: (amount: number) => string;
}

export function BookingPipeline({
  created,
  confirmed,
  inProgress,
  completed,
  cancelled,
  noShow,
  revenue = 0,
  walkIns = 0,
  customers = 0,
  avgTicket = 0,
  utilization = 0,
  activeBarbersNow = 0,
  totalChairs = 0,
  queueLength = 0,
  avgWaitTime = 0,
  formatCurrency = (n) => `AED ${n.toLocaleString()}`,
}: BookingPipelineProps) {
  const total = created || 1;
  const completionRate = Math.round((completed / total) * 100);
  const dropOffRate = Math.round(((cancelled + noShow) / total) * 100);

  // Key metrics for the top row
  const keyMetrics = [
    {
      label: 'Revenue',
      value: formatCurrency(revenue),
      subtext: avgTicket > 0 ? `${formatCurrency(avgTicket)} avg ticket` : undefined,
      icon: Wallet,
    },
    {
      label: 'Bookings',
      value: created,
      subtext: `${completed} completed`,
      icon: TrendingUp,
    },
    {
      label: 'Walk-ins',
      value: walkIns,
      subtext: created > 0 ? `${Math.round((walkIns / created) * 100)}% of total` : undefined,
      icon: UserPlus,
    },
    {
      label: 'Customers',
      value: customers,
      subtext: undefined,
      icon: Users,
    },
    {
      label: 'Utilization',
      value: `${utilization}%`,
      subtext: `${activeBarbersNow}/${totalChairs} barbers active`,
      icon: Percent,
    },
    {
      label: 'Queue',
      value: queueLength,
      subtext: avgWaitTime > 0 ? `~${avgWaitTime} min avg wait` : 'No wait',
      icon: Clock,
    },
  ];

  // Pipeline stages
  const pipelineStages = [
    { label: 'Created', value: created, pct: 100 },
    { label: 'Confirmed', value: confirmed, pct: Math.round((confirmed / total) * 100) },
    { label: 'In Progress', value: inProgress, pct: Math.round((inProgress / total) * 100) },
    { label: 'Completed', value: completed, pct: completionRate },
  ];

  return (
    <div className="card-bookdesk !p-0 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-border bg-muted/30">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-foreground">Operations Summary</h3>
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span>Completion: <span className="font-semibold text-foreground">{completionRate}%</span></span>
            <span>Drop-off: <span className="font-semibold">{dropOffRate}%</span></span>
          </div>
        </div>
      </div>

      {/* Key Metrics Row */}
      <div className="grid grid-cols-3 lg:grid-cols-6 gap-px bg-border">
        {keyMetrics.map((metric) => {
          const Icon = metric.icon;
          return (
            <div key={metric.label} className="bg-background p-4">
              <div className="flex items-center gap-2 mb-1.5">
                <Icon className="h-4 w-4 text-muted-foreground" />
                <span className="text-xs text-muted-foreground uppercase tracking-wide">{metric.label}</span>
              </div>
              <p className="text-xl font-bold tabular-nums">{metric.value}</p>
              {metric.subtext && (
                <p className="text-xs text-muted-foreground mt-0.5">{metric.subtext}</p>
              )}
            </div>
          );
        })}
      </div>

      {/* Pipeline Flow */}
      <div className="p-4 border-t border-border">
        <p className="text-xs text-muted-foreground uppercase tracking-wide mb-3">Booking Pipeline</p>
        <div className="flex items-center gap-2">
          {pipelineStages.map((stage, index) => (
            <div key={stage.label} className="flex items-center flex-1">
              <div className="flex-1 p-3 rounded-lg bg-muted/40 text-center">
                <div className="text-2xl font-bold tabular-nums">{stage.value}</div>
                <div className="text-xs text-muted-foreground mt-1">{stage.label}</div>
                <div className="text-xs text-muted-foreground">{stage.pct}%</div>
              </div>
              {index < pipelineStages.length - 1 && (
                <ChevronRight className="h-5 w-5 text-muted-foreground/40 flex-shrink-0 mx-1" />
              )}
            </div>
          ))}
        </div>

        {/* Dropout stats inline */}
        <div className="mt-4 pt-3 border-t border-border/50 flex items-center justify-between text-sm">
          <div className="flex items-center gap-6">
            <span className="text-muted-foreground">
              Cancelled: <span className="font-semibold text-foreground">{cancelled}</span>
              <span className="text-muted-foreground/70 ml-1">({Math.round((cancelled / total) * 100)}%)</span>
            </span>
            <span className="text-muted-foreground">
              No-show: <span className="font-semibold text-foreground">{noShow}</span>
              <span className="text-muted-foreground/70 ml-1">({Math.round((noShow / total) * 100)}%)</span>
            </span>
          </div>
          <span className="text-muted-foreground">
            {created} total bookings
          </span>
        </div>
      </div>
    </div>
  );
}
